import React, { useState, useEffect } from "react";
import { Modal, Box, SpaceBetween } from "@cloudscape-design/components";
import { TreeSelect, Select } from "antd";
import { Button } from "../Button";
import { Workflow } from "@amzn/finspace-service-typescript-client";
import { PERIOD_TREE_DATA } from "../../../constants/mfr-constants";
import { NETPPM_PERIOD_TREE_DATA, stripWeekPrefix, getPPMPeriodTreeByScenario, SCENARIO_WEEKLY, addWeekPrefix } from "../../../constants/netppm-constants";
import { getComparisonScenarioOptions } from "../../../utils/comparisonScenarioUtils";
import { getPeriodTitle, findCurrentPeriodValue } from "../../../utils/PeriodGenerator";
import { netppmFormSchema } from "../../NetPPM/schemas/netppm-form-schema";
import { mfrFormSchema } from "../../MFR/schemas/mfr-form-schema";
import { deriveCategory } from "../../../utils/activityTransformUtils";
import { FormThemeProvider } from "../Forms/FormThemeProvider";
import { getLabelByValue } from "../../../utils/optionUtils";

interface RunWorkflowModalProps {
  visible: boolean;
  workflow: Workflow | null;
  loading?: boolean;
  onRun: (input: any, presentationContext?: any) => void;
  onCancel: () => void;
}

export const RunWorkflowModal: React.FC<RunWorkflowModalProps> = ({
  visible,
  workflow,
  loading = false,
  onRun,
  onCancel,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>("");
  const [selectedComparisonScenario, setSelectedComparisonScenario] = useState<
    string[]
  >([]);
  const [comparisonScenarioOptions, setComparisonScenarioOptions] = useState<
    any[]
  >([]);
  const [previousPeriod, setPreviousPeriod] = useState<string>("");

  // Derive category from workflow tags
  const category = deriveCategory(workflow?.tags)?.toUpperCase();
  const isMFRWorkflow = workflow?.tags?.includes("MFR");
  const isPPMWorkflow = workflow?.tags?.includes("PPM");

  // Helper to extract value from object or return as-is (for backward compatibility)
  const extractValue = (item: any): string => {
    if (typeof item === 'object' && item !== null && 'value' in item) {
      return item.value;
    }
    return item;
  };

  // Determine PPM period tree based on scenario
  const ppmScenarioRaw = isPPMWorkflow ? (workflow?.input as any)?.scenarios : null;
  const ppmScenario = Array.isArray(ppmScenarioRaw) 
    ? extractValue(ppmScenarioRaw[0]) 
    : extractValue(ppmScenarioRaw);
  const ppmPeriodTreeData = getPPMPeriodTreeByScenario(ppmScenario);


  // Update comparison scenario options when period changes
  useEffect(() => {
    if (isMFRWorkflow && selectedPeriod) {
      const options = getComparisonScenarioOptions(selectedPeriod);
      setComparisonScenarioOptions(options);

      // Reset comparison scenario only if period actually changed (not initial load)
      if (previousPeriod && previousPeriod !== selectedPeriod) {
        setSelectedComparisonScenario([]);
      }

      setPreviousPeriod(selectedPeriod);
    }
  }, [selectedPeriod, isMFRWorkflow, previousPeriod]);

  // Get options from schemas
  const mfrComparisonScenarioField = mfrFormSchema.sections[1].fields.find(
    (f) => f.name === "comparison_scenario"
  ) as any;

  // Pre-populate with workflow's existing values when modal opens
  useEffect(() => {
    if (visible && workflow?.input && (isMFRWorkflow || isPPMWorkflow)) {
      const workflowInput = workflow.input as any;

      // Handle period for both MFR and PPM
      const workflowPeriod = workflowInput?.period;
      if (workflowPeriod) {
        const rawPeriodValue = Array.isArray(workflowPeriod)
          ? extractValue(workflowPeriod[0])
          : extractValue(workflowPeriod);
        
        // Map to current tree value (add week- prefix for weekly scenarios)
        const treeData = isMFRWorkflow ? PERIOD_TREE_DATA : ppmPeriodTreeData;
        const lookupValue = addWeekPrefix(rawPeriodValue, ppmScenario);
        const periodValue = findCurrentPeriodValue(treeData, lookupValue);
        setSelectedPeriod(periodValue);

        // Set comparison scenario options for MFR based on loaded period
        if (isMFRWorkflow) {
          const options = getComparisonScenarioOptions(periodValue);
          setComparisonScenarioOptions(options);

          // Set comparison scenario values AFTER setting options
          const workflowComparisonScenario = workflowInput?.comparison_scenario;
          if (
            workflowComparisonScenario &&
            Array.isArray(workflowComparisonScenario)
          ) {
            // Extract values from objects
            const comparisonValues = workflowComparisonScenario.map(extractValue);
            setSelectedComparisonScenario(comparisonValues);
          }
        }
      }
    }
  }, [visible, workflow, isMFRWorkflow, isPPMWorkflow, ppmPeriodTreeData]);

  const handleRun = () => {
    // Validate required fields for MFR workflows
    if (isMFRWorkflow) {
      const periodValid = selectedPeriod ? true : false;
      const comparisonValid =
        selectedComparisonScenario && selectedComparisonScenario.length > 0;

      if (!periodValid || !comparisonValid) {
        return;
      }
    }

    const getFieldLabel = (
      fieldName: string,
      value: string,
      options: any[]
    ) => {
      return getLabelByValue(options, value);
    };

    if (isMFRWorkflow) {
      // MFR: period + comparison scenario as arrays of objects
      const periodArray = Array.isArray(selectedPeriod)
        ? selectedPeriod
        : [selectedPeriod].filter(Boolean);
      const comparisonScenarioArray = Array.isArray(selectedComparisonScenario)
        ? selectedComparisonScenario
        : [selectedComparisonScenario].filter(Boolean);

      // Get labels for values
      const buildLookupMap = (nodes: any[]): Record<string, string> => {
        const map: Record<string, string> = {};
        const processNode = (node: any): void => {
          map[node.value] = node.title;
          if (node.children?.length) {
            node.children.forEach(processNode);
          }
        };
        nodes.forEach(processNode);
        return map;
      };

      const periodLabelMap = buildLookupMap(PERIOD_TREE_DATA);
      const periodLabels = periodArray.map((p) => periodLabelMap[p] || getPeriodTitle(p) || p);

      const comparisonLabels = comparisonScenarioArray.map((c) =>
        getFieldLabel("comparison_scenario", c, comparisonScenarioOptions)
      );

      // Create input objects with both label and value
      const periodObjects = periodArray.map((value, index) => ({
        label: periodLabels[index],
        value,
      }));

      const comparisonScenarioObjects = comparisonScenarioArray.map((value, index) => ({
        label: comparisonLabels[index],
        value,
      }));

      // Build presentation context with updated values
      const updatedPresentationContext = {
        period: periodLabels,
        comparison_scenario: comparisonLabels,
        report: {
          period: periodObjects,
          comparison_scenario: comparisonScenarioObjects,
        },
      };

      // Send objects with label+value to the API
      onRun(
        { 
          period: periodObjects, 
          comparison_scenario: comparisonScenarioObjects 
        },
        updatedPresentationContext
      );
    } else if (isPPMWorkflow) {
      // PPM: period as array of objects
      const periodValue = selectedPeriod;
      
      if (!periodValue) return;
      
      // Get label from tree structure
      const buildLookupMap = (nodes: any[]): Record<string, string> => {
        const map: Record<string, string> = {};
        const processNode = (node: any): void => {
          map[node.value] = node.title;
          if (node.children?.length) {
            node.children.forEach(processNode);
          }
        };
        nodes.forEach(processNode);
        return map;
      };
      
      const periodLabelMap = buildLookupMap(ppmPeriodTreeData);
      const periodLabel = periodLabelMap[periodValue] || getPeriodTitle(periodValue) || periodValue;
      
      onRun({ 
        period: [{ label: periodLabel, value: stripWeekPrefix(periodValue) }] 
      });
    } else {
      // Other workflows: empty input
      onRun({});
    }
  };

  return (
    <FormThemeProvider>
      <Modal
        visible={visible}
        onDismiss={onCancel}
        header={`Run ${category} Workflow`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="secondary" onClick={onCancel} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleRun} loading={loading}>
                Run Report
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <Box variant="p">
            {isMFRWorkflow
              ? `Select the reporting period and comparison scenario for your ${category} analysis. The report will be generated using your current workflow configuration for the selected options.`
              : isPPMWorkflow
              ? `Select the reporting period for your ${category} analysis. The report will be generated using your current workflow configuration for the selected period.`
              : `Run your ${category} workflow with its current configuration.`}
          </Box>

          <Box>
            <Box variant="strong">Running: {workflow?.name}</Box>
          </Box>

          {(isMFRWorkflow || isPPMWorkflow) && (
            <Box>
              <Box variant="strong" padding={{ bottom: "xs" }}>
                Select Period
              </Box>
              <TreeSelect
                virtual={false}
                value={selectedPeriod || null}
                onChange={(value) => {
                  // Use null consistently for cleared state
                  setSelectedPeriod(value ?? null);
                }}
                treeData={isMFRWorkflow ? PERIOD_TREE_DATA : ppmPeriodTreeData}
                placeholder="Select period..."
                treeDefaultExpandAll={false}
                {...(!isMFRWorkflow && { treeExpandAction: "click" })}
                showSearch
                allowClear
                style={{ width: "100%" }}
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
              />
              {isMFRWorkflow && !selectedPeriod && (
                <Box
                  color="text-status-error"
                  fontSize="body-s"
                  padding={{ top: "xs" }}
                >
                  Period is required
                </Box>
              )}
            </Box>
          )}

          {isMFRWorkflow && (
            <Box>
              <Box variant="strong" padding={{ bottom: "xs" }}>
                Comparison Scenario
              </Box>
              <Select
                mode="multiple"
                value={selectedComparisonScenario}
                onChange={(values) => {
                  // Ensure we handle clearing properly
                  setSelectedComparisonScenario(values || []);
                }}
                options={comparisonScenarioOptions}
                placeholder={
                  selectedPeriod
                    ? "Select comparison scenario..."
                    : "Select Period First"
                }
                disabled={!selectedPeriod}
                maxTagCount={2}
                allowClear
                style={{ width: "100%" }}
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
              />
              {!selectedComparisonScenario ||
              selectedComparisonScenario.length === 0 ? (
                <Box
                  color="text-status-error"
                  fontSize="body-s"
                  padding={{ top: "xs" }}
                >
                  Comparison Scenario is required
                </Box>
              ) : null}
            </Box>
          )}

          {!isMFRWorkflow && !isPPMWorkflow && (
            <Box variant="p" color="text-body-secondary">
              This workflow will run with its current configuration.
            </Box>
          )}

          <Box variant="p" color="text-body-secondary">
            {isMFRWorkflow
              ? "The report will be generated for the selected period and comparison scenario with the current configuration."
              : isPPMWorkflow
              ? "The report will be generated for the selected period with the current configuration."
              : "The workflow will execute with its existing configuration."}
          </Box>
        </SpaceBetween>
      </Modal>
    </FormThemeProvider>
  );
};
